import axios from 'utils/api';
import { distributeQuantityAcrossRecommendations } from 'utils/distribute-quantity';

export type QuotedAccessoryRowType = {
  productId: number;
  quantity: number;
  zoneUniqueId: string | null;
  condenserUniqueId?: string | null;
};

type QuotableSystemRowType = {
  uniqueId: string;
  accessories: QuotedAccessoryRowType[];
};

export type CondenserZoneIdsType = {
  condenserUniqueId: string;
  zoneUniqueIds: string[];
};

export type ZoneQuotedAccessoriesMapType = Record<
  string,
  Array<{ productId: number; quantity: number }>
>;

function addToZoneMap(
  zoneMap: ZoneQuotedAccessoriesMapType,
  zoneUniqueId: string,
  productId: number,
  quantity: number
): ZoneQuotedAccessoriesMapType {
  if (quantity <= 0) return zoneMap;
  const list = [...(zoneMap[zoneUniqueId] ?? [])];
  const existing = list.find((entry) => entry.productId === productId);
  if (existing) {
    existing.quantity += quantity;
  } else {
    list.push({ productId, quantity });
  }
  return { ...zoneMap, [zoneUniqueId]: list };
}

export function buildZoneQuotedAccessoriesMap(
  accessories: QuotedAccessoryRowType[],
  condensers: CondenserZoneIdsType[]
): ZoneQuotedAccessoriesMapType {
  const zonesByCondenser = new Map(
    condensers.map((condenser) => [
      condenser.condenserUniqueId,
      condenser.zoneUniqueIds
    ])
  );

  return accessories.reduce<ZoneQuotedAccessoriesMapType>(
    (zoneMap, accessory) => {
      if (accessory.quantity <= 0) return zoneMap;

      if (accessory.zoneUniqueId) {
        return addToZoneMap(
          zoneMap,
          accessory.zoneUniqueId,
          accessory.productId,
          accessory.quantity
        );
      }

      if (!accessory.condenserUniqueId) return zoneMap;
      const zoneUniqueIds = zonesByCondenser.get(accessory.condenserUniqueId);
      if (!zoneUniqueIds || zoneUniqueIds.length === 0) return zoneMap;

      const distributed = distributeQuantityAcrossRecommendations(
        accessory.quantity,
        zoneUniqueIds.map((uniqueId) => ({ uniqueId }))
      );
      const allocated = distributed.reduce(
        (sum, entry) => sum + entry.quantity,
        0
      );
      const leftover = accessory.quantity - allocated;
      return distributed.reduce(
        (nextMap, { uniqueId, quantity }, index) =>
          addToZoneMap(
            nextMap,
            uniqueId,
            accessory.productId,
            index === distributed.length - 1 ? quantity + leftover : quantity
          ),
        zoneMap
      );
    },
    {}
  );
}

export async function fetchZoneAccessoriesBySystem(
  systems: Array<{
    uniqueId: string;
    condensers: CondenserZoneIdsType[];
  }>
): Promise<Record<string, ZoneQuotedAccessoriesMapType>> {
  if (systems.length === 0) return {};
  try {
    const { data } = await axios.post<QuotableSystemRowType[]>(
      'quotable-systems',
      { uniqueIds: systems.map((system) => system.uniqueId) }
    );
    const condensersBySystem = new Map(
      systems.map((system) => [system.uniqueId, system.condensers])
    );
    const bySystem: Record<string, ZoneQuotedAccessoriesMapType> = {};
    data.forEach((quotableSystem) => {
      bySystem[quotableSystem.uniqueId] = buildZoneQuotedAccessoriesMap(
        quotableSystem.accessories,
        condensersBySystem.get(quotableSystem.uniqueId) ?? []
      );
    });
    return bySystem;
  } catch {
    return {};
  }
}
